import type { RouteObject } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import {
  canAccessProjectIntegrationSettings,
  canAccessProjectPlusButtonOptions,
  canAccessProjectQuickActions,
  canAccessProjectSettings,
  canAccessProjectStyling,
} from "@/components/ProjectSidebar/permissions";
import { RequireAuth } from "@/components/Routing/RequireAuth";
import { RequireFeatureFlag } from "@/components/Routing/RequireFeatureFlag";

import { projectRoutes } from "./routes";

export const projectsRoute = [
  {
    path: projectRoutes.edit,
    lazy: async () => {
      const { EditProjectPage } = await import("./pages/create-or-edit/EditProjectPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectSettings}>
            <EditProjectPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.styling,
    lazy: async () => {
      const { ProjectStylingPage } = await import("./pages/styling/ProjectStylingPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectStyling}>
            <ProjectStylingPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.plusButtonOptions,
    lazy: async () => {
      const { PlusButtonOptionsPage } = await import("./pages/plus-button-options/PlusButtonOptionsPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectPlusButtonOptions}>
            <PlusButtonOptionsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.quickActions,
    lazy: async () => {
      const { QuickActionsPage } = await import("./pages/quick-actions/QuickActionsPage");

      return {
        element: (
          <RequireFeatureFlag flag="isQuickActionConfigurationAvailable">
            <RequireAuth permission={canAccessProjectQuickActions}>
              <QuickActionsPage />
            </RequireAuth>
          </RequireFeatureFlag>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrations,
    lazy: async () => {
      const { IntegrationsPage } = await import("./pages/integrations/pages/IntegrationsPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <IntegrationsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationZiezodan,
    lazy: async () => {
      const { ZiezodanPage } = await import("./pages/integrations/pages/ZiezodanPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <ZiezodanPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationRems,
    lazy: async () => {
      const { RemsPage } = await import("./pages/integrations/pages/RemsPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <RemsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationRemsAddresses,
    lazy: async () => {
      const { RemsReconciliationPage } = await import("./pages/integrations/pages/RemsReconciliationPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <RemsReconciliationPage dataType="addresses" />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationRemsResidents,
    lazy: async () => {
      const { RemsReconciliationPage } = await import("./pages/integrations/pages/RemsReconciliationPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <RemsReconciliationPage dataType="residents" />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationHomeDna,
    lazy: async () => {
      const { HomeDnaPage } = await import("./pages/integrations/pages/HomeDnaPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <HomeDnaPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationDico,
    lazy: async () => {
      const { DicoPage } = await import("./pages/integrations/pages/DicoPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <DicoPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationOutlook,
    lazy: async () => {
      const { OutlookPage } = await import("./pages/integrations/pages/OutlookPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <OutlookPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationJim,
    lazy: async () => {
      const { JimPage } = await import("./pages/integrations/pages/JimPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <JimPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationIqbi,
    lazy: async () => {
      const { IqbiPage } = await import("./pages/integrations/pages/IqbiPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <IqbiPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationMyPup,
    lazy: async () => {
      const { MyPupPage } = await import("./pages/integrations/pages/MyPupPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <MyPupPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationBringme,
    lazy: async () => {
      const { BringmePage } = await import("./pages/integrations/pages/BringmePage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <BringmePage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationCopilot,
    lazy: async () => {
      const { CopilotPage } = await import("./pages/integrations/pages/CopilotPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <CopilotPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationSwoogo,
    lazy: async () => {
      const { SwoogoPage } = await import("./pages/integrations/pages/SwoogoPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <SwoogoPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationOfficeRnd,
    lazy: async () => {
      const { OfficeRndPage } = await import("./pages/integrations/pages/OfficeRndPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <OfficeRndPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationHubSpot,
    lazy: async () => {
      const { HubSpotPage } = await import("./pages/integrations/pages/HubSpotPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <HubSpotPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationHealthDistrict,
    lazy: async () => {
      const { HealthDistrictPage } = await import("./pages/integrations/pages/HealthDistrictPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <HealthDistrictPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationWunderbricks,
    lazy: async () => {
      const { WunderbricksPage } = await import("./pages/integrations/pages/WunderbricksPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <WunderbricksPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationILoq,
    lazy: async () => {
      const { ILoqPage } = await import("./pages/integrations/pages/ILoqPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <ILoqPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: projectRoutes.integrationJansenHuybregts,
    lazy: async () => {
      const { JansenHuybregtsPage } = await import("./pages/integrations/pages/JansenHuybregtsPage");

      return {
        element: (
          <RequireAuth permission={canAccessProjectIntegrationSettings}>
            <JansenHuybregtsPage />
          </RequireAuth>
        ),
      };
    },
  },
  { path: "*", element: <ErrorPage status={404} /> },
] satisfies RouteObject[];
